/**
 * LogViewer — log list with graph/action-plan modals and export/import.
 *
 * Verbatin port of the standalone `LogViewer.tsx` with:
 *  - Bonus E: photo thumbnail rendering
 *  - Bonus G: ImportLogsButton placement
 *  - Bonus D: per-entry Print link
 */
import { useState } from 'react';
import { classifyResult } from '@reefapp/shared';
import type { TestLogEntry } from '@reefapp/shared';
import { GraphModal } from './GraphModal';
import { ActionPlanModal } from './ActionPlanModal';
import { ExportLogsButton } from './ExportLogsButton';
import { ImportLogsButton } from './ImportLogsButton';

const formatDate = (isoString: string) =>
  new Date(isoString).toLocaleString();

export interface LogViewerProps {
  logs: ReadonlyArray<TestLogEntry>;
  onDeleteLog(id: string): void;
  onImport(merged: TestLogEntry[]): void;
}

export function LogViewer({ logs, onDeleteLog, onImport }: LogViewerProps) {
  const [graphParam, setGraphParam] = useState<TestLogEntry['parameterKey'] | null>(null);
  const [planLog, setPlanLog] = useState<TestLogEntry | null>(null);

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h2 className="text-3xl font-bold text-slate-200">Test Logs</h2>
        <div className="flex gap-2">
          <ImportLogsButton existingLogs={logs} onImport={onImport} />
          <ExportLogsButton logs={logs} />
        </div>
      </div>
      {logs.length > 0 ? (
        <div className="space-y-4">
          {logs.map((log) => {
            const status = classifyResult(log.parameterKey, log.result);
            const statusLabel =
              status === 'low'
                ? 'Low'
                : status === 'high'
                  ? 'High'
                  : status === 'ideal'
                    ? 'Ideal'
                    : 'N/A';
            const statusColor =
              status === 'low'
                ? 'bg-yellow-500'
                : status === 'high'
                  ? 'bg-red-500'
                  : status === 'ideal'
                    ? 'bg-green-500'
                    : 'bg-slate-400';

            return (
              <div
                key={log.id}
                className="bg-slate-800 p-4 rounded-lg shadow-md border border-slate-700 space-y-3"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-bold text-lg text-slate-200">
                      {log.parameter}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatDate(log.date)}
                    </p>
                    <p className="text-sm text-slate-400">
                      {log.brand} - {log.testName}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-2xl text-cyan-400">
                      {String(log.result)}{' '}
                      <span className="text-lg text-slate-500 font-medium">
                        {log.unit}
                      </span>
                    </p>
                    <span
                      className={`text-xs font-bold px-2 py-1 rounded-full text-white ${statusColor}`}
                    >
                      {statusLabel}
                    </span>
                  </div>
                </div>
                {log.notes && (
                  <p className="text-sm bg-slate-700/50 p-3 rounded-md border-l-4 border-slate-600 text-slate-400 italic">
                    &ldquo;{log.notes}&rdquo;
                  </p>
                )}
                {log.photoDataUrl && (
                  <div className="mt-2">
                    <img
                      src={log.photoDataUrl}
                      alt="Comparator photo"
                      className="max-h-32 rounded border border-slate-600 cursor-pointer"
                      onClick={(e) => {
                        const target = e.currentTarget;
                        if (target.style.maxHeight === 'none') {
                          target.style.maxHeight = '8rem';
                        } else {
                          target.style.maxHeight = 'none';
                        }
                      }}
                    />
                  </div>
                )}
                <div className="flex justify-end items-center gap-2 pt-2 border-t border-slate-700">
                  <button
                    onClick={() => setPlanLog(log)}
                    className="text-xs font-semibold bg-blue-500/20 text-blue-300 px-3 py-1 rounded-full hover:bg-blue-500/30"
                  >
                    Action Plan
                  </button>
                  <button
                    onClick={() => setGraphParam(log.parameterKey)}
                    className="text-xs font-semibold bg-green-500/20 text-green-300 px-3 py-1 rounded-full hover:bg-green-500/30"
                  >
                    Graph
                  </button>
                  <button
                    onClick={() => {
                      window.print();
                    }}
                    className="text-xs font-semibold bg-slate-500/20 text-slate-300 px-3 py-1 rounded-full hover:bg-slate-500/30"
                  >
                    Print
                  </button>
                  <button
                    onClick={() => onDeleteLog(log.id)}
                    className="text-xs font-semibold bg-red-500/20 text-red-300 px-3 py-1 rounded-full hover:bg-red-500/30"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-16 px-6 bg-slate-800 rounded-lg shadow-md border border-slate-700">
          <svg
            aria-hidden="true"
            width="64"
            height="64"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="mx-auto text-slate-600"
          >
            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V7l-4-4H7L3 7z" />
            <path d="M12 15v-3m0-3h.01" />
          </svg>
          <h3 className="mt-4 text-xl font-semibold text-slate-300">
            No Logs Yet
          </h3>
          <p className="mt-2 text-slate-500">
            Perform a test from the 'Test Kits' tab to start
            logging your aquarium's parameters.
          </p>
        </div>
      )}

      {graphParam && (
        <GraphModal
          logs={logs}
          parameterKey={graphParam}
          onClose={() => setGraphParam(null)}
        />
      )}
      {planLog && (
        <ActionPlanModal
          log={planLog}
          onClose={() => setPlanLog(null)}
        />
      )}
    </div>
  );
}
